import { useState } from "react";
import { Calendar, GraduationCap, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AddDashboardModal (Iter 9.30)
 *
 * Replaces the legacy chain of native window.prompt() calls that shipped on
 * /me Hosting tile when a host clicked "Add another dashboard". Per product
 * direction:
 *   - Only TWO add-on options: Event Dashboard + Instructor Dashboard.
 *     Both are free-host dashboards that can be standalone OR linked to an
 *     existing service-provider dashboard. Linked dashboards share a single
 *     RSVP across them.
 *   - Submit goes through the normal interest intake → admin activation flow.
 *     Status starts at "dashboard_pending"; host receives email when admin
 *     activates it.
 *
 * Backend: POST /api/host/dashboard-requests { dashboard_type, linked_event_id,
 *           category, display_name, rationale, type_choice }
 */
export default function AddDashboardModal({ open, onOpenChange, org }) {
  const t = useAdminCopy();
  const [step, setStep] = useState(1);
  const [typeChoice, setTypeChoice] = useState(null); // "event" | "instructor"
  const [displayName, setDisplayName] = useState("");
  const [rationale, setRationale] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(1);
    setTypeChoice(null);
    setDisplayName("");
    setRationale("");
    setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    if (!displayName.trim()) {
      toast.error("Please give this dashboard a name");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/host/dashboard-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dashboard_type: typeChoice,
          type_choice: typeChoice,
          linked_event_id: org?.events?.[0]?.event_id,
          category: typeChoice === "instructor" ? "instructor" : "events",
          display_name: displayName.trim(),
          rationale: rationale.trim(),
          status: "dashboard_pending",
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Request sent. Admin will activate within 24 hours.");
      handleClose(false);
    } catch (err) {
      toast.error("Could not send request: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0" data-testid="add-dashboard-modal">
        <DialogHeader className="p-5 pb-3 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Plus className="w-4 h-4 text-indigo-600" />
            {t("me.add_dashboard.title", "Add another dashboard")}
          </DialogTitle>
          <p className="text-xs text-gray-500 mt-1">
            {t("me.add_dashboard.subtitle", "Free add-on dashboards. Goes through admin review → activation in 24h.")}
          </p>
        </DialogHeader>

        {step === 1 && (
          <div className="p-5 space-y-3">
            <p className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider">
              What kind of dashboard?
            </p>
            <button
              onClick={() => { setTypeChoice("event"); setStep(2); }}
              className="w-full text-left rounded-xl border border-gray-200 hover:border-indigo-400 transition-all p-4 flex items-start gap-3"
              data-testid="add-dashboard-event"
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">{t("me.add_dashboard.event.label", "Experience Dashboard")}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t("me.add_dashboard.event.desc", "Run a one-off experience with intake, RSVPs, and confirmation. Standalone or linked to your provider dashboard so clients RSVP once.")}
                </p>
              </div>
            </button>

            <button
              onClick={() => { setTypeChoice("instructor"); setStep(2); }}
              className="w-full text-left rounded-xl border border-gray-200 hover:border-indigo-400 transition-all p-4 flex items-start gap-3"
              data-testid="add-dashboard-instructor"
            >
              <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">{t("me.add_dashboard.instructor.label", "Instructor Dashboard")}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t("me.add_dashboard.instructor.desc", "Teach classes / lessons (e.g. \"Luxe Lessons\"). Linked to your provider dashboard so students share a single profile across both.")}
                </p>
              </div>
            </button>

            <p className="text-[10px] text-gray-400 pt-2 leading-relaxed">
              These are the only free-host dashboards available now. Style, Nails and additional categories are on the waitlist - they'll open as we launch each one.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="p-5 space-y-4">
            <button
              onClick={() => setStep(1)}
              className="text-[11px] text-indigo-600 hover:underline"
              data-testid="add-dashboard-back"
            >
              ← Change type
            </button>
            <div>
              <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider mb-1">Type</p>
              <p className="text-sm font-medium capitalize">{typeChoice} dashboard</p>
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider block mb-1">
                Dashboard name *
              </label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={typeChoice === "instructor" ? "e.g. Luxe Lessons" : "e.g. Bridal Suite"}
                data-testid="add-dashboard-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider block mb-1">
                What will you use this for? (helps admin activate faster)
              </label>
              <Textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="A few sentences about the audience, schedule, expected client volume..."
                rows={4}
                data-testid="add-dashboard-rationale"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => handleClose(false)}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting || !displayName.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="add-dashboard-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit for activation
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
